import { track } from '@/services/analytics'
import { StorageService } from '@/utils/storage'

export class OnboardingManager {
  private currentStep: number = 1
  private totalSteps: number = 5
  private overlay: HTMLElement | null = null
  private onComplete: () => void

  constructor(onComplete: () => void) {
    this.onComplete = onComplete
    this.overlay = document.getElementById('onboardingOverlay')
    this.setupEventListeners()
  }

  private setupEventListeners(): void {
    
    // Next buttons for steps 2-5
    const nextBtns = document.querySelectorAll('.onboarding-next-btn')
    nextBtns.forEach(btn => {
      btn.addEventListener('click', () => this.nextStep())
    })

    // Finish button for step 5
    const finishBtn = document.querySelector('.onboarding-finish-btn')
    finishBtn?.addEventListener('click', () => this.completeOnboarding())


  }



  public shouldShow(userId: string): boolean {
    return !StorageService.isOnboardingCompleted(userId)
  }

  public static resetOnboarding(userId: string): void {
    const key = `onboarding-completed-${userId}`
    localStorage.removeItem(key)
    console.log('Onboarding reset! Refresh the page to see the onboarding flow.')
  }

  public show(): void {
    if (!this.overlay) return

    this.overlay.style.display = 'flex'
    this.currentStep = 1
    this.showStep(1)
    
    // Track onboarding start
    track('onboarding_started')
  }

  public hide(): void {
    if (!this.overlay) return
    
    this.overlay.style.display = 'none'
    this.removeHighlights()
  }

  private isVisible(): boolean {
    return this.overlay?.style.display === 'flex'
  }

  private showStep(stepNumber: number): void {
    // Hide all steps
    const allSteps = document.querySelectorAll('.onboarding-step')
    allSteps.forEach(step => {
      step.classList.remove('active')
    })

    // Show current step
    const currentStepElement = document.getElementById(`onboarding-step-${stepNumber}`)
    if (currentStepElement) {
      currentStepElement.classList.add('active')
    }

    // Handle step-specific logic
    this.handleStepSpecificLogic(stepNumber)
    
    // Track step view
    track('onboarding_step_viewed', { step: stepNumber })
  }

  private handleStepSpecificLogic(stepNumber: number): void {
    switch (stepNumber) {
      case 1:
        // Simple welcome step, no special logic needed
        break
      case 2:
        this.animateRarityItems()
        break
      case 3:
        this.animatePathOptions()
        break
      case 4:
        this.animateFeatureItems()
        break
      case 5:
        this.animateRewardItems()
        break
    }
  }

  private nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++
      this.showStep(this.currentStep)
    }
  }



  private completeOnboarding(): void {
    const userId = this.getUserId()
    StorageService.setOnboardingCompleted(userId)
    
    track('onboarding_completed', { 
      completed_step: this.currentStep,
      total_steps: this.totalSteps 
    })
    
    this.hide()
    this.onComplete()
  }

  private removeHighlights(): void {
    // Remove any UI element highlights
    const highlightedElements = document.querySelectorAll('.onboarding-ui-highlight')
    highlightedElements.forEach(el => {
      el.classList.remove('onboarding-ui-highlight')
    })
  }

  private animateRarityItems(): void {
    const rarityItems = document.querySelectorAll('.rarity-item')
    rarityItems.forEach((item, index) => {
      setTimeout(() => {
        item.classList.add('animate-in')
      }, index * 100)
    })
  }

  private animatePathOptions(): void {
    const pathOptions = document.querySelectorAll('.path-option')
    pathOptions.forEach((option, index) => {
      setTimeout(() => {
        option.classList.add('animate-in')
      }, index * 200)
    })
  }

  private animateFeatureItems(): void {
    const featureItems = document.querySelectorAll('.feature-item')
    featureItems.forEach((item, index) => {
      setTimeout(() => {
        item.classList.add('animate-in')
      }, index * 150)
    })
  }

  private animateRewardItems(): void {
    const rewardItems = document.querySelectorAll('.reward-item')
    rewardItems.forEach((item, index) => {
      setTimeout(() => {
        item.classList.add('animate-in')
      }, index * 200)
    })
  }



  private getUserId(): string {
    // Get user ID from Telegram or fallback
    return window.Telegram?.WebApp?.initDataUnsafe?.user?.id || 'anonymous'
  }

  // Public method to check if onboarding is active
  public isActive(): boolean {
    return this.isVisible()
  }

  // Public method to get current step
  public getCurrentStep(): number {
    return this.currentStep
  }
}